/**
 * SdsDataSheetPage — AI research SDS-attribute result detail screen (Figma "SDS Data sheet", 6429:2318).
 * Reached from AiResearchCard's VIEW RESULTS. Renders the selected SDS document,
 * the extracted attributes table, and the SDS attribute based recommendation.
 */

import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { Button, Divider, Icon } from "@amzn/meridian";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import documentTokens from "@amzn/meridian-tokens/base/icon/document";
import { ClassifyWasteProfileResponse } from "src/types/classificationTypes";
import { ContainerUsingFor, ItemFlowContext } from "src/types/containerTypes";
import PillButton from "../../UI/PillButton";
import sdsDocIcon from "src/images/sds-document-icon.png";
import "./classification.scss";

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)}KB`;
  return `${(kb / 1024).toFixed(1)}MB`;
}

interface SdsDataSheetState {
  result: ClassifyWasteProfileResponse;
  asin: string;
  itemName?: string;
  isEligibleForDonation?: boolean;
  itemFlowContext: ItemFlowContext;
  donationCategoryIds: string[];
}

const SdsDataSheetPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state as SdsDataSheetState | undefined;

  if (!state?.result) {
    return (
      <Column width="100%" height="100vh" alignmentHorizontal="center" alignmentVertical="center" spacing="400">
        <Text>No AI research results available.</Text>
        <PillButton text="Go Back" color="secondary" onClick={() => navigate(-1)} />
      </Column>
    );
  }

  const { result, asin, itemName, isEligibleForDonation, itemFlowContext, donationCategoryIds } = state;
  const { sdsDocument, sdsSource, sdsAttributes, recommendedWasteProfilesIds, reasoning } = result;

  const goToContainer = (usingFor: ContainerUsingFor) => {
    navigate("/tote/chooseContainer", {
      state: {
        itemFlowContext,
        wasteProfileIds: usingFor === "WASTE" ? recommendedWasteProfilesIds : [],
        donationCategoryIds: usingFor === "DONATION" ? donationCategoryIds : [],
        usingFor,
      },
    });
  };

  return (
    <Column className="app-gradient-bg pwa-scrollable" spacing="450" spacingInset="450 450 600">
      <Row alignmentVertical="center" spacingInset="400 none">
        <Button type="icon" onClick={() => navigate(-1)}>
          <Icon color="success" tokens={arrowLeftTokens} />
        </Button>
        <Text type="h100">AI Assisted Analysis</Text>
      </Row>

      <Column spacing="none" spacingInset="none 200">
        {itemName && <Text type="h300">{itemName}</Text>}
        <Text type="b200" color="secondary">{`ASIN: ${asin}`}</Text>
      </Column>

      {sdsSource === "DOCUMENT" && sdsDocument && (
        <Column className="sds-document-card" width="100%" spacingInset="400" spacing="300">
          <Row alignmentVertical="center" spacing="400">
            <img className="sds-document-card__icon" src={sdsDocIcon} alt="SDS document" />
            <Column spacing="none">
              <Text type="b300">{sdsDocument.fileName}</Text>
              <Text type="b200" color="secondary">
                {`Size: ${formatFileSize(sdsDocument.fileSizeBytes)}`}
              </Text>
            </Column>
            <button
              className="sds-document-card__view"
              onClick={() => window.open(sdsDocument.url, "_blank", "noopener,noreferrer")}
            >
              VIEW
            </button>
          </Row>
          <Divider />
          <Row spacing="100" alignmentVertical="top">
            <Text type="b200" color="secondary">
              Source:
            </Text>
            <a
              className="sds-document-card__source"
              href={sdsDocument.url}
              target="_blank"
              rel="noreferrer"
            >
              {sdsDocument.url}
            </a>
          </Row>
          {sdsAttributes && sdsAttributes.length > 0 && (
            <Column spacing="300" spacingInset="400 none none">
              <Divider />
              <Row width="100%" widths={["50%", "fill"]}>
                <Text type="h100">Attributes</Text>
                <Text type="h100">Values</Text>
              </Row>
              <Divider />
              {sdsAttributes.map((attribute, idx) => (
                <React.Fragment key={idx}>
                  {idx > 0 && <Divider />}
                  <Row width="100%" widths={["50%", "fill"]} alignmentVertical="top" spacing="300">
                    <Text type="b200" color="secondary">
                      {attribute.name}
                    </Text>
                    <Text type="b200">{attribute.value}</Text>
                  </Row>
                </React.Fragment>
              ))}
            </Column>
          )}
        </Column>
      )}

      <Column className="home-card asin-waste-profile--sds" width="100%" spacingInset="400 none none">
        <Row alignmentVertical="center" spacing="300" spacingInset="none 500">
          <Icon color="inverted" tokens={documentTokens} />
          <Text type="h200" color="inverted">
            SDS Attribute Based Recommendation
          </Text>
        </Row>
        <Column className="home-card" spacingInset="none">
          <Column spacingInset="400 500" spacing="300">
            {recommendedWasteProfilesIds.map((profileId, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && <Divider />}
                <Text type="h200">{profileId}</Text>
              </React.Fragment>
            ))}
            {reasoning && (
              <Text type="b200" color="secondary">
                {reasoning}
              </Text>
            )}
          </Column>
        </Column>
      </Column>

      <Column className="asin-sticky-footer" spacing="300">
        <Row spacing="300" alignmentHorizontal="center">
          <PillButton
            text="DONATE"
            color="outline"
            onClick={() => goToContainer("DONATION")}
            disabled={isEligibleForDonation !== true}
          />
          <PillButton text="ADD TO WASTE" color="success" onClick={() => goToContainer("WASTE")} />
        </Row>
      </Column>
    </Column>
  );
};

export default SdsDataSheetPage;
